import type { AnalyticsEventName, AnalyticsEvents } from "./events";

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    gtag?: Gtag;
    dataLayer?: unknown[];
  }
}

export const GA_ID = "G-SPZKTHP2PN";

// Solo se mide el sitio de producción: localhost, Staging y los previews de
// Netlify no ensucian GA4 ni Clarity.
const PRODUCTION_HOSTS = ["ciudad-viva.cl", "www.ciudad-viva.cl"];

// Navegadores del equipo: tampoco se miden. Se marcan solos al entrar como
// superadmin, o a mano abriendo cualquier página con ?interno=1 (?interno=0
// lo quita). Queda guardado en ese navegador.
const INTERNAL_KEY = "cv_internal";

function readInternalParam(): void {
  const value = new URLSearchParams(window.location.search).get("interno");
  if (value === "1") markInternal(true);
  else if (value === "0") markInternal(false);
}

function isInternal(): boolean {
  try {
    return localStorage.getItem(INTERNAL_KEY) === "1";
  } catch {
    return false;
  }
}

export function markInternal(on: boolean): void {
  try {
    if (on) localStorage.setItem(INTERNAL_KEY, "1");
    else localStorage.removeItem(INTERNAL_KEY);
  } catch {}
  if (!on) return;
  // Corta en el acto lo que ya estaba cargado en esta pestaña.
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_ID}`] = true;
  try {
    (window as unknown as { clarity?: (cmd: string) => void }).clarity?.("stop");
  } catch {}
}

// Se lee al cargar el módulo, antes del primer render en el navegador.
if (typeof window !== "undefined") readInternalParam();

export function analyticsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return PRODUCTION_HOSTS.includes(window.location.hostname) && !isInternal() && !isAutomated();
}

// Navegadores manejados por un programa (Playwright, Puppeteer, Selenium):
// el estándar WebDriver los obliga a marcarse así. Bots que se hacen pasar
// por navegador quedan fuera aunque el server no los detecte por su UA.
function isAutomated(): boolean {
  return typeof navigator !== "undefined" && navigator.webdriver === true;
}

// Métricas internas (/admin/estadisticas: visitas, secciones, eventos,
// juegos): se miden siempre, sin depender del aviso de analítica, pero solo
// en producción —o en local, que escribe en la base de desarrollo— y nunca
// del equipo ni de navegadores automatizados. Staging y los previews de
// Netlify no cuentan.
export function internalMetricsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const measuredHost = PRODUCTION_HOSTS.includes(host) || host === "localhost";
  return measuredHost && !isInternal() && !isAutomated();
}

// Crea la cola de gtag (lo mismo que el snippet oficial). GA4 se carga
// SIEMPRE para medir visitas (páginas, origen del tráfico, país, dispositivo)
// por interés legítimo; los eventos de acciones solo van con consentimiento
// (ver track). Sin señales de Google ni personalización de anuncios. Las
// visitas en navegación interna las registra GA4 solo ("medición mejorada" →
// cambios del historial del navegador).
// Sin medición (ver analyticsEnabled) window.gtag no existe y track y
// setUserProperties no hacen nada.
export function initGtag(): void {
  if (typeof window === "undefined" || window.gtag || !analyticsEnabled()) return;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js espera el objeto `arguments`, no un array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { allow_google_signals: false, allow_ad_personalization_signals: false });
}

// Eventos de acciones disparados antes de leer el consentimiento guardado
// (p. ej. event_view al montar la página, antes del efecto de
// AnalyticsManager). Se envían si el consentimiento guardado es "granted"; si
// no, se descartan. Mientras la persona NO ha decidido, no se envían.
let consentResolved = false;
let eventsGranted = false;
let pending: [string, object][] = [];

export function resolveConsent(granted: boolean): void {
  consentResolved = true;
  eventsGranted = granted;
  if (granted) for (const [name, params] of pending) window.gtag?.("event", name, params);
  pending = [];
}

// Eventos de acciones (búsquedas, guardados, compartidos...): solo con
// consentimiento. Las visitas de página no pasan por acá.
export function track<N extends AnalyticsEventName>(name: N, params: AnalyticsEvents[N]): void {
  if (typeof window === "undefined") return;
  if (!consentResolved) {
    if (pending.length < 20) pending.push([name, params]);
    return;
  }
  if (!eventsGranted || !window.gtag) return;
  try {
    window.gtag("event", name, params);
  } catch {
    // La analítica nunca debe romper la página.
  }
}

// Para enlaces en componentes de servidor (sin onClick): AnalyticsManager
// escucha los clics y manda el evento de cualquier elemento con estos atributos.
//   <a href={...} {...trackAttrs("movie_provider_click", { ... })}>
export const TRACK_EVENT_ATTR = "data-track-event";
export const TRACK_PARAMS_ATTR = "data-track-params";

export function trackAttrs<N extends AnalyticsEventName>(
  name: N,
  params: AnalyticsEvents[N],
): Record<string, string> {
  return { [TRACK_EVENT_ATTR]: name, [TRACK_PARAMS_ATTR]: JSON.stringify(params) };
}

// Propiedades de usuario (anónimas): idioma, si inició sesión y su rol.
export function setUserProperties(props: Record<string, string>): void {
  if (typeof window === "undefined" || !window.gtag) return;
  try {
    window.gtag("set", "user_properties", props);
  } catch {}
}
